
import { useState, useCallback, useEffect } from "react";
import {
  Button,
  EmptyState,
  FormField,
  Heading,
  Input,
  Row,
  Textarea,
  Badge,
  Text,
  Modal,
  Alert,
  Spinner,
  useToast,
} from "@/components/ds";
import { MapPinIcon } from "@/icons/MapPinIcon";
import { ChevronRightIcon } from "@/icons/ChevronRightIcon";
import type { Site } from "@/domain/sites/types";
import { SiteMap } from "./SiteMapLazy";
import { RadiusPicker } from "./RadiusPicker";
import { useGeocode } from "./useGeocode";
import styles from "./SiteList.module.css";

interface SiteListProps {
  readonly initialSites: readonly Site[];
}

interface SiteFormState {
  name: string;
  address: string;
  postcode: string;
  gpsRadiusMetres: string;
  latitude: number | null;
  longitude: number | null;
  clientContactName: string;
  clientContactEmail: string;
  notes: string;
}

function siteToFormState(site: Site): SiteFormState {
  return {
    name: site.name,
    address: site.address ?? "",
    postcode: site.postcode ?? "",
    gpsRadiusMetres: String(site.gpsRadiusMetres),
    latitude: site.latitude,
    longitude: site.longitude,
    clientContactName: site.clientContactName ?? "",
    clientContactEmail: site.clientContactEmail ?? "",
    notes: site.notes ?? "",
  };
}

function ExpandedSiteForm({
  site,
  formState,
  errors,
  saving,
  geocodeWarning,
  onUpdateField,
  onUpdateLocation,
  onSave,
  onDelete,
  onDismissWarning,
}: {
  readonly site: Site;
  readonly formState: SiteFormState;
  readonly errors: Record<string, string>;
  readonly saving: boolean;
  readonly geocodeWarning: string | null;
  readonly onUpdateField: (field: keyof SiteFormState, value: string) => void;
  readonly onUpdateLocation: (lat: number | null, lng: number | null) => void;
  readonly onSave: () => void;
  readonly onDelete: () => void;
  readonly onDismissWarning: () => void;
}) {
  const [detailsOpen, setDetailsOpen] = useState(
    !!(site.clientContactName || site.clientContactEmail || site.notes),
  );
  const [notFoundDismissed, setNotFoundDismissed] = useState(false);

  const hasExistingPin = formState.latitude != null || site.latitude != null;
  const {
    location: geocodedLocation,
    pendingLocation,
    loading: geocoding,
    notFound,
    acceptPending,
    dismissPending,
  } = useGeocode(formState.postcode, hasExistingPin);

  useEffect(() => {
    if (geocodedLocation && formState.latitude == null) {
      onUpdateLocation(geocodedLocation.latitude, geocodedLocation.longitude);
    }
  }, [geocodedLocation, formState.latitude, onUpdateLocation]);

  useEffect(() => {
    if (notFound) {
      setNotFoundDismissed(false);
    }
  }, [notFound]);

  const mapLat = formState.latitude ?? geocodedLocation?.latitude ?? site.latitude;
  const mapLng = formState.longitude ?? geocodedLocation?.longitude ?? site.longitude;

  const handlePostcodeChange = useCallback((value: string) => {
    onUpdateField("postcode", value);
  }, [onUpdateField]);

  const handleAcceptPending = useCallback(() => {
    if (pendingLocation) {
      onUpdateLocation(pendingLocation.latitude, pendingLocation.longitude);
      acceptPending();
    }
  }, [pendingLocation, onUpdateLocation, acceptPending]);

  return (
    <div className={styles.expandedBody}>
      <div className={styles.fieldsColumn}>
        {geocodeWarning && (
          <Alert variant="warning" onDismiss={onDismissWarning}>
            {geocodeWarning}
          </Alert>
        )}

        <FormField label="Site name" htmlFor={`name-${site.id}`} error={errors.name} required>
          <Input
            id={`name-${site.id}`}
            name="name"
            value={formState.name}
            onChange={(e) => onUpdateField("name", e.target.value)}
            error={!!errors.name}
          />
        </FormField>

        <FormField label="Address" htmlFor={`address-${site.id}`} error={errors.address}>
          <Input
            id={`address-${site.id}`}
            name="address"
            value={formState.address}
            onChange={(e) => onUpdateField("address", e.target.value)}
            error={!!errors.address}
          />
        </FormField>

        <FormField label="Postcode" htmlFor={`postcode-${site.id}`} error={errors.postcode}>
          <Input
            id={`postcode-${site.id}`}
            name="postcode"
            value={formState.postcode}
            onChange={(e) => handlePostcodeChange(e.target.value)}
            error={!!errors.postcode}
          />
        </FormField>

        <button
          type="button"
          className={styles.disclosureButton}
          onClick={() => setDetailsOpen(!detailsOpen)}
          aria-expanded={detailsOpen}
        >
          <ChevronRightIcon
            width={16}
            height={16}
            className={`${styles.disclosureChevron} ${detailsOpen ? styles.disclosureOpen : ""}`}
          />
          <span>Contact and notes</span>
        </button>

        {detailsOpen && (
          <div className={styles.detailsSection}>
            <FormField label="Contact name" htmlFor={`contact-name-${site.id}`}>
              <Input
                id={`contact-name-${site.id}`}
                name="clientContactName"
                value={formState.clientContactName}
                onChange={(e) => onUpdateField("clientContactName", e.target.value)}
              />
            </FormField>

            <FormField label="Contact email" htmlFor={`contact-email-${site.id}`} error={errors.clientContactEmail}>
              <Input
                id={`contact-email-${site.id}`}
                name="clientContactEmail"
                type="email"
                value={formState.clientContactEmail}
                onChange={(e) => onUpdateField("clientContactEmail", e.target.value)}
                error={!!errors.clientContactEmail}
              />
            </FormField>

            <FormField label="Notes" htmlFor={`notes-${site.id}`} hint="Visible to cleaners on their shift view">
              <Textarea
                id={`notes-${site.id}`}
                name="notes"
                value={formState.notes}
                onChange={(e) => onUpdateField("notes", e.target.value)}
                rows={2}
              />
            </FormField>
          </div>
        )}

        <div className={styles.actions}>
          <Button onClick={onSave} disabled={saving} size="sm">
            {saving ? "Saving..." : "Save changes"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
          >
            Remove site
          </Button>
        </div>
      </div>

      <div className={styles.mapColumn}>
        <div className={styles.mapContainer}>
          {notFound && !geocoding && !notFoundDismissed && (
            <div className={styles.locationPrompt}>
              <Text size="sm">We couldn&apos;t find that postcode. Click the map to place a pin.</Text>
              <div className={styles.promptButtons}>
                <Button size="sm" variant="ghost" onClick={() => setNotFoundDismissed(true)}>Dismiss</Button>
              </div>
            </div>
          )}
          {pendingLocation && (
            <div className={styles.locationPrompt}>
              <Text size="sm">New location found. Use it?</Text>
              <div className={styles.promptButtons}>
                <Button size="sm" onClick={handleAcceptPending}>Update</Button>
                <Button size="sm" variant="ghost" onClick={dismissPending}>Keep current</Button>
              </div>
            </div>
          )}
          <SiteMap
            latitude={mapLat}
            longitude={mapLng}
            radiusMetres={Number(formState.gpsRadiusMetres)}
            onLocationChange={(lat, lng) => { onUpdateLocation(lat, lng); setNotFoundDismissed(true); }}
          />
          {geocoding && (
            <div className={styles.mapLoading}>
              <Spinner size="sm" />
            </div>
          )}
        </div>

        <RadiusPicker
          value={formState.gpsRadiusMetres}
          onChange={(value) => onUpdateField("gpsRadiusMetres", value)}
        />
      </div>
    </div>
  );
}

export function SiteList({ initialSites }: SiteListProps) {
  const { toast } = useToast();
  const [sites, setSites] = useState<readonly Site[]>(initialSites);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [formState, setFormState] = useState<SiteFormState | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [geocodeWarning, setGeocodeWarning] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Site | null>(null);
  const [deleting, setDeleting] = useState(false);

  const toggleSite = useCallback((site: Site) => {
    if (expandedId === site.id) {
      setExpandedId(null);
      setFormState(null);
      setErrors({});
      setGeocodeWarning(null);
    } else {
      setExpandedId(site.id);
      setFormState(siteToFormState(site));
      setErrors({});
      setGeocodeWarning(null);
    }
  }, [expandedId]);

  const updateField = useCallback((field: keyof SiteFormState, value: string) => {
    setFormState((prev) => prev ? { ...prev, [field]: value } : null);
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const updateLocation = useCallback((lat: number | null, lng: number | null) => {
    setFormState((prev) => prev ? { ...prev, latitude: lat, longitude: lng } : null);
  }, []);

  const handleSave = useCallback(async () => {
    if (!expandedId || !formState) return;

    setSaving(true);
    setErrors({});
    setGeocodeWarning(null);

    try {
      const response = await fetch(`/api/sites/${expandedId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formState.name,
          address: formState.address || null,
          postcode: formState.postcode || null,
          latitude: formState.latitude,
          longitude: formState.longitude,
          gpsRadiusMetres: Number(formState.gpsRadiusMetres),
          clientContactName: formState.clientContactName || null,
          clientContactEmail: formState.clientContactEmail || null,
          notes: formState.notes || null,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        if (data.error?.fields) {
          setErrors(data.error.fields);
        } else {
          toast("error", data.error?.message ?? "Something went wrong. Please try again.");
        }
        return;
      }

      setSites((prev) => prev.map((s) => (s.id === expandedId ? data.data.site : s)));
      setFormState(siteToFormState(data.data.site));

      if (data.data.geocodeWarning) {
        setGeocodeWarning(data.data.geocodeWarning);
      }

      toast("success", "Site updated");
    } finally {
      setSaving(false);
    }
  }, [expandedId, formState, toast]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    try {
      const response = await fetch(`/api/sites/${deleteTarget.id}`, {
        method: "DELETE",
      });

      const data = await response.json();
      if (!data.success) {
        toast("error", "Something went wrong. Please try again.");
        return;
      }

      setSites((prev) => prev.filter((s) => s.id !== deleteTarget.id));
      if (expandedId === deleteTarget.id) {
        setExpandedId(null);
        setFormState(null);
      }
      setDeleteTarget(null);
      toast("success", "Site removed");
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, expandedId, toast]);

  if (sites.length === 0) {
    return (
      <div className={styles.page}>
        <EmptyState
          icon={<MapPinIcon width={48} height={48} />}
          title="Your cleaning sites"
          description="Add your first cleaning site to get started"
          action={<Button href="/sites/new">Add a site</Button>}
        />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <Heading level={1} size="h3">Sites</Heading>
        <Button href="/sites/new" size="sm">Add site</Button>
      </div>

      <div className={styles.tableCard}>
        {sites.map((site) => {
          const isExpanded = expandedId === site.id;

          return (
            <div key={site.id} className={styles.siteEntry}>
              <button
                type="button"
                className={styles.siteRow}
                onClick={() => toggleSite(site)}
                aria-expanded={isExpanded}
              >
                <span className={styles.siteName}>{site.name}</span>
                <span className={styles.sitePostcode}>{site.postcode ?? ""}</span>
                <span className={styles.gpsBadge}>
                  {site.latitude !== null ? (
                    <Badge variant="success">GPS</Badge>
                  ) : (
                    <Badge variant="neutral">No GPS</Badge>
                  )}
                </span>
                <ChevronRightIcon
                  width={16}
                  height={16}
                  className={`${styles.chevron} ${isExpanded ? styles.chevronOpen : ""}`}
                />
              </button>

              {isExpanded && formState && (
                <ExpandedSiteForm
                  site={site}
                  formState={formState}
                  errors={errors}
                  saving={saving}
                  geocodeWarning={geocodeWarning}
                  onUpdateField={updateField}
                  onUpdateLocation={updateLocation}
                  onSave={handleSave}
                  onDelete={() => setDeleteTarget(site)}
                  onDismissWarning={() => setGeocodeWarning(null)}
                />
              )}
            </div>
          );
        })}
      </div>

      <Modal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="Remove site"
        size="sm"
        footer={
          <Row justify="end" gap={3}>
            <Button variant="secondary" size="sm" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" size="sm" onClick={handleDelete} disabled={deleting}>
              {deleting ? "Removing..." : "Yes, remove it"}
            </Button>
          </Row>
        }
      >
        <Text>Are you sure you want to remove this site?</Text>
        <Text size="sm" color="muted">
          Past attendance records and reports will still be available.
        </Text>
      </Modal>
    </div>
  );
}
